import { Fields, YesObject } from '@yes.technology/react-toolkit'
import {
  FetchRelationFn,
  GetNestedRelationDataProps,
  RelationCardData,
  UseNestedRelationDataProps
} from './NestedRelationCard.types'
import { useQuery } from '@tanstack/react-query'
import fetchYesObjects from 'yesObject/shared/api/fetchYesObjects'

const getLabelFromFields = (column: string, fields: Fields) =>
  fields[column]?.display_text.des || column

const resolvePath = async (
  object: YesObject,
  pathParts: string[],
  fetchRelation: FetchRelationFn
): Promise<string[]> => {
  // Base case: We reached the end of the configured path
  if (pathParts.length === 0) {
    return [object.des]
  }

  const [currentKey, ...remainingPath] = pathParts
  let nextValue = object[currentKey]

  // If the relation is missing, fetch it dynamically using the deepest available object
  if (nextValue === undefined || nextValue === null) {
    try {
      const fetchedData = await fetchRelation(object)
      if (fetchedData && fetchedData[currentKey]) {
        nextValue = fetchedData[currentKey]
      } else {
        // Fetch failed to provide the path or returned nothing; halt traversal
        return []
      }
    } catch (error) {
      // Fail the request / halt further fetching on error
      return []
    }
  }

  if (Array.isArray(nextValue)) {
    const tasks = nextValue.map((item) =>
      resolvePath(item, remainingPath, fetchRelation)
    )
    const results = await Promise.all(tasks)
    return results.flat()
  }

  if (typeof nextValue === 'object') {
    return await resolvePath(nextValue, remainingPath, fetchRelation)
  }

  // If it's a primitive string/value but we still have path left, it's a dead end
  return remainingPath.length === 0 ? [nextValue] : []
}

export const nestValuesBySource = (
  values: { label: string; value: string; source: string }[]
): RelationCardData[] => {
  const valuesBySource = new Map<string, typeof values>()

  for (const v of values) {
    const existing = valuesBySource.get(v.source) || []
    valuesBySource.set(v.source, [...existing, v])
  }

  const findParent = (source: string): string | null => {
    const parts = source.split('.')

    // First, check if any ancestor exists in valuesBySource
    for (let i = parts.length - 1; i >= 1; i--) {
      const parent = parts.slice(0, i).join('.')
      if (valuesBySource.has(parent)) return parent
    }

    // If no ancestor exists in valuesBySource, check if this source is the shallowest
    // among sources with the same first part
    if (parts.length > 1) {
      const firstPart = parts[0]
      // Find the minimum depth among sources with the same first part
      const sourcesWithSamePrefix = values
        .filter((v) => v.source.startsWith(firstPart + '.'))
        .map((v) => v.source.split('.').length)

      if (sourcesWithSamePrefix.length > 0) {
        const minDepth = Math.min(...sourcesWithSamePrefix)
        // If this source is deeper than the shallowest, it's a child
        if (parts.length > minDepth) {
          return firstPart
        }
      }
    }

    return null
  }

  const build = (source: string): RelationCardData | RelationCardData[] => {
    // Check if this source exists in the Map
    const items = valuesBySource.get(source)

    // Get children of this source
    const children = values.filter((v) => findParent(v.source) === source)
    if (!children.length) {
      // No children - return the items (if any)
      if (!items) return []
      return items.map((i) => ({ label: i.label, value: i.value }))
    }

    const nested = children.flatMap((c) => build(c.source))

    if (!items) {
      // Virtual parent - just return nested wrapped
      return [{ value: nested }]
    }

    return [
      ...items.map((i) => ({ label: i.label, value: i.value })),
      { value: nested }
    ]
  }

  // Get unique root sources and process each once
  const rootSources = [
    ...new Set(values.filter((v) => !findParent(v.source)).map((v) => v.source))
  ]

  // Also get unique virtual parents (first parts) that have children but aren't in valuesBySource
  const allParents = new Set(
    values.map((v) => findParent(v.source)).filter(Boolean)
  ) as Set<string>
  const virtualParents = [...allParents].filter((p) => !valuesBySource.has(p))

  // Process root sources - wrap if they have children
  const result = rootSources.flatMap((source) => {
    const buildResult = build(source)
    if (!Array.isArray(buildResult)) {
      return buildResult
    }
    const hasChildren = values.some((v) => findParent(v.source) === source)
    return hasChildren ? [{ value: buildResult }] : buildResult
  })

  const virtualResults = virtualParents.flatMap((parent) => build(parent))

  return [...result, ...virtualResults]
}

export const getNestedRelationData = async ({
  object,
  columns,
  fields,
  sourcesConfig,
  fetchRelation
}: GetNestedRelationDataProps) => {
  const results: RelationCardData[] = []

  for (const column of columns) {
    const columnPaths = sourcesConfig[column] || [
      { label: getLabelFromFields(column, fields), source: '' }
    ]

    const columnResults: { label: string; value: string; source: string }[] = []

    for (const { label, source } of columnPaths) {
      const pathParts = [column, ...(source ? source.split('.') : [])]

      const values = await resolvePath(object, pathParts, fetchRelation)

      columnResults.push(
        ...values.map((value) => ({
          label,
          value,
          source: pathParts.join('.')
        }))
      )
    }

    results.push(
      ...(Array.isArray(object[column])
        ? [
            {
              value: nestValuesBySource(columnResults)
            }
          ]
        : columnResults.map(({ label, value }) => ({ label, value })))
    )
  }

  return results
}

const useNestedRelationData = ({
  columns,
  sourcesConfig,
  fields,
  object,
  enabled
}: UseNestedRelationDataProps) => {
  const { data, isLoading } = useQuery<RelationCardData[]>({
    enabled,
    queryKey: ['nestedRelationData', columns, sourcesConfig, fields, object],
    queryFn: () =>
      object
        ? getNestedRelationData({
            columns,
            sourcesConfig,
            fields,
            object,
            fetchRelation: async (relationObject) => {
              const response = await fetchYesObjects({
                filterBody: {
                  orderby: [],
                  pagination: {
                    limit: 1
                  },
                  objectclass: relationObject.uuid_Objectclass,
                  query: {
                    $and: [
                      { 'uuid.des': { $eq: relationObject.uuid } },
                      {
                        'situation.des': {
                          $gte: globalThis.activeSituationCode
                        }
                      }
                    ]
                  }
                }
              })

              if (!response.objects) {
                return Promise.reject(new Error('Object not found'))
              }

              return response.objects[0]
            }
          })
        : Promise.reject(new Error('No input'))
  })

  return { data, isLoading }
}

export default useNestedRelationData
